export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function clamp(num, min, max) {
  return Math.max(min, Math.min(max, num))
}

export function uid() {
  if (window.crypto && crypto.randomUUID) return crypto.randomUUID()
  return 'cand-' + Date.now() + '-' + Math.random().toString(16).slice(2)
}

export function avg(values) {
  const valid = values.filter((v) => typeof v === 'number' && !Number.isNaN(v))
  return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0
}

export function scoreDimension(answers, items, dimension) {
  const dimItems = items.filter((item) => item.dimension === dimension)
  let sum = 0
  let count = 0
  dimItems.forEach((item) => {
    const val = answers[item.id]
    if (val != null) {
      sum += item.reverse ? 6 - val : val
      count++
    }
  })
  return count > 0 ? sum / count : 0
}

// Nota média (1 a 5) das questões de múltipla escolha do questionário: cada item de escala vale a resposta
// (6 - resposta nos itens invertidos) e cada cenário vale a média das pontuações da alternativa "melhor".
// Retorna null quando a ficha não tem nenhuma resposta objetiva.
export function calculateAverageScore(sections, answers, sjtAnswers) {
  const points = []
  sections.forEach((section) => {
    const items = section.items || []
    const scenarios = section.scenarios || []
    items.forEach((item) => {
      const val = answers?.[item.id]
      if (typeof val === 'number') points.push(item.reverse ? 6 - val : val)
    })
    scenarios.forEach((scenario) => {
      const best = scenario.options.find((option) => option.id === sjtAnswers?.[scenario.id]?.best)
      if (best) points.push(avg(Object.values(best.scores)))
    })
  })
  return points.length ? avg(points) : null
}

export function calculateScores(sections, answers, sjtAnswers) {
  const hexacoItems = sections.find((s) => s.id === 'hexaco')?.items || []
  const regItems = sections.find((s) => s.id === 'regulatory')?.items || []
  const sdtItems = sections.find((s) => s.id === 'sdt')?.items || []
  const gritItems = sections.find((s) => s.id === 'grit')?.items || []
  const sjtSection = sections.find((s) => s.id === 'sjt')

  const CI = scoreDimension(answers, gritItems, 'CI')
  const PE = scoreDimension(answers, gritItems, 'PE')

  let proactivity = 0
  let integrityScore = 0
  let teamwork = 0
  let sjtCount = 0

  ;(sjtSection?.scenarios || []).forEach((scenario) => {
    const ans = sjtAnswers[scenario.id]
    if (ans?.best) {
      const bestOpt = scenario.options.find((option) => option.id === ans.best)
      if (bestOpt) {
        proactivity += bestOpt.scores.proactivity
        integrityScore += bestOpt.scores.integrity
        teamwork += bestOpt.scores.teamwork
        sjtCount++
      }
    }
  })

  if (sjtCount > 0) {
    proactivity /= sjtCount
    integrityScore /= sjtCount
    teamwork /= sjtCount
  }

  return {
    H: scoreDimension(answers, hexacoItems, 'H'),
    E: scoreDimension(answers, hexacoItems, 'E'),
    X: scoreDimension(answers, hexacoItems, 'X'),
    A: scoreDimension(answers, hexacoItems, 'A'),
    C: scoreDimension(answers, hexacoItems, 'C'),
    O: scoreDimension(answers, hexacoItems, 'O'),
    promotion: scoreDimension(answers, regItems, 'promotion'),
    prevention: scoreDimension(answers, regItems, 'prevention'),
    autonomy: scoreDimension(answers, sdtItems, 'autonomy'),
    competence: scoreDimension(answers, sdtItems, 'competence'),
    relatedness: scoreDimension(answers, sdtItems, 'relatedness'),
    CI,
    PE,
    grit: avg([CI, PE]),
    proactivity,
    integrity: integrityScore,
    teamwork,
  }
}

export function getProfileArchetype(scores) {
  const H = scores.H
  const C = scores.C
  const promotion = scores.promotion
  const prevention = scores.prevention
  const grit = scores.grit
  const autonomy = scores.autonomy
  const competence = scores.competence
  const relatedness = scores.relatedness
  const proactivity = scores.proactivity

  let archetype = ''
  let strengths = []
  let risks = []
  let managementStyle = ''
  let idealRole = ''
  const motivators = []
  const demotivators = []

  if (C >= 4 && H >= 4 && grit >= 4) {
    archetype = 'O Pilar'
    strengths = ['Confiabilidade extrema', 'Entrega consistente', 'Integridade sob pressão', 'Execução metódica']
    risks = ['Rigidez em situações que exigem flexibilidade', 'Pode travar em ambiguidade', 'Baixa tolerância a colegas menos disciplinados']
    idealRole = 'Contencioso complexo, compliance, revisão técnica e rotinas com prazos críticos.'
    managementStyle = 'Delegar com autonomia, alinhar padrão esperado e cobrar aderência com clareza. Evitar microgerenciamento.'
  } else if (promotion >= 4 && scores.X >= 3.5 && scores.O >= 3.5) {
    archetype = 'O Motor'
    strengths = ['Iniciativa', 'Criatividade para resolver problemas', 'Boa energia social', 'Velocidade de execução']
    risks = ['Pode sacrificar precisão por velocidade', 'Pode perder interesse em projetos longos', 'Pode gerar atrito com perfis muito cautelosos']
    idealRole = 'Consultoria criativa, relacionamento, novos produtos, frentes de crescimento e exposição ao cliente.'
    managementStyle = 'Metas ambiciosas, feedback rápido, reconhecimento visível e cobrança firme por qualidade.'
  } else if (prevention >= 4 && C >= 3.5 && scores.E >= 3) {
    archetype = 'O Guardião'
    strengths = ['Precisão', 'Atenção ao detalhe', 'Baixo índice de erros', 'Cuidado regulatório']
    risks = ['Paralisia por análise', 'Aversão a riscos necessários', 'Dificuldade com mudanças abruptas']
    idealRole = 'Revisão de peças, controle de qualidade, compliance, due diligence e operações sensíveis.'
    managementStyle = 'Dar contexto, procedimento, previsibilidade e segurança antes de aumentar pressão por velocidade.'
  } else if (autonomy >= 4.3 && grit >= 3.5 && proactivity >= 4) {
    archetype = 'O Autônomo'
    strengths = ['Auto-gestão forte', 'Ownership natural', 'Resolve problemas sem supervisão intensa', 'Sustenta projetos complexos']
    risks = ['Pode resistir a controles excessivos', 'Pode isolar soluções', 'Pode sofrer com processos rígidos demais']
    idealRole = 'Pesquisa jurídica, projetos especiais, teses complexas e frentes com autonomia.'
    managementStyle = 'Definir entregável, prazo e contexto. Dar espaço de execução e check-ins mais espaçados.'
  } else if (relatedness >= 4.3 && scores.A >= 4 && scores.X >= 3.5) {
    archetype = 'O Conector'
    strengths = ['Boa cola social', 'Facilita comunicação', 'Ajuda colegas', 'Fortalece clima e confiança']
    risks = ['Pode evitar conflitos necessários', 'Pode priorizar harmonia em detrimento do resultado', 'Pode se sobrecarregar ajudando os outros']
    idealRole = 'Atendimento, coordenação, integração de time, onboarding e áreas com interface constante.'
    managementStyle = 'Valorizar contribuição relacional, dar senso de pertencimento e cobrar resultado sem gerar culpa excessiva.'
  } else if (competence >= 4.3 && scores.O >= 3.5 && C >= 3) {
    archetype = 'O Técnico'
    strengths = ['Profundidade analítica', 'Busca por excelência técnica', 'Aprendizado rápido', 'Respeito por expertise']
    risks = ['Pode desvalorizar tarefas simples', 'Pode se frustrar em ambiente pouco exigente', 'Pode ter dificuldade em traduzir complexidade']
    idealRole = 'Pesquisa, pareceres, teses de alta complexidade e análise aprofundada.'
    managementStyle = 'Dar desafio técnico crescente, feedback específico e ambiente com exigência intelectual real.'
  } else {
    archetype = 'O Generalista'
    strengths = ['Adaptabilidade', 'Versatilidade', 'Equilíbrio entre frentes', 'Capacidade de aprender em contexto diverso']
    risks = ['Pode se dispersar', 'Pode demorar a construir um diferencial forte', 'Pode precisar de direção mais clara para especialização']
    idealRole = 'Função coringa, rotação entre frentes, suporte a múltiplas áreas até definição de vocação.'
    managementStyle = 'Testar em diferentes contextos, observar padrão de melhor performance e então direcionar com mais foco.'
  }

  const sdtMax = Math.max(autonomy, competence, relatedness)
  if (sdtMax === autonomy) {
    motivators.push('Liberdade para decidir como executar', 'Baixo microgerenciamento')
    demotivators.push('Controle excessivo', 'Burocracia sem propósito claro')
  }
  if (sdtMax === competence) {
    motivators.push('Desafio técnico crescente', 'Feedback claro sobre evolução')
    demotivators.push('Tarefas repetitivas sem complexidade', 'Falta de critério ou de feedback')
  }
  if (sdtMax === relatedness) {
    motivators.push('Senso de equipe e pertencimento', 'Confiança do gestor e do time')
    demotivators.push('Isolamento constante', 'Ambiente hostil ou competitivo demais')
  }

  if (promotion > prevention) {
    motivators.push('Metas ambiciosas com reconhecimento', 'Espaço para iniciativa e inovação')
    demotivators.push('Excesso de rigidez e conformidade')
  } else {
    motivators.push('Segurança, clareza e previsibilidade', 'Reconhecimento por precisão e cuidado')
    demotivators.push('Mudança brusca sem preparação')
  }

  return { archetype, strengths, risks, managementStyle, idealRole, motivators, demotivators }
}

export function estimateFitAndRetention(scores, areaText) {
  const area = String(areaText || '').toLowerCase()
  let fit = 35 + avg([scores.H, scores.C, scores.integrity, scores.teamwork, scores.grit, scores.competence]) * 11
  let retention = 26 + avg([scores.grit, scores.relatedness, scores.autonomy, scores.competence, scores.A]) * 11

  if (/tribut|fiscal|tax/.test(area)) {
    fit += (scores.C - 3) * 4 + (scores.competence - 3) * 4 + (scores.O - 3) * 2
  }
  if (/contenc|process|litig/.test(area)) {
    fit += (scores.grit - 3) * 4 + (scores.prevention - 3) * 3 + (scores.C - 3) * 3
  }
  if (/consult|societ|contrat|planej/.test(area)) {
    fit += (scores.O - 3) * 4 + (scores.competence - 3) * 3 + (scores.promotion - 3) * 2
  }
  if (/comercial|relacion|clientes|negócios|negocios|desenvolvimento/.test(area)) {
    fit += (scores.X - 3) * 4 + (scores.relatedness - 3) * 3 + (scores.promotion - 3) * 3
  }
  if (/compliance|controle|qualidade|auditoria/.test(area)) {
    fit += (scores.H - 3) * 4 + (scores.prevention - 3) * 4 + (scores.C - 3) * 3
  }

  retention += (scores.grit - 3) * 4 + (scores.relatedness - 3) * 3
  if (scores.E > 4.2 && scores.prevention > 4.2) retention -= 4
  if (scores.autonomy < 2.8) retention -= 4
  if (scores.relatedness < 2.8) retention -= 5

  fit = Math.round(clamp(fit, 18, 97))
  retention = Math.round(clamp(retention, 16, 96))

  const fitDrivers = []
  if (scores.H >= 4) fitDrivers.push('boa integridade')
  if (scores.C >= 4) fitDrivers.push('boa disciplina de execução')
  if (scores.grit >= 4) fitDrivers.push('boa sustentação de esforço')
  if (scores.competence >= 4) fitDrivers.push('forte orientação a domínio técnico')
  if (scores.X >= 4 && /clientes|comercial|relacion|negócios|negocios/.test(area)) fitDrivers.push('boa energia social para a área')
  if (!fitDrivers.length) fitDrivers.push('perfil geral utilizável com necessidade de observar alocação ideal')

  const retentionDrivers = []
  if (scores.relatedness >= 4) retentionDrivers.push('tende a responder bem a pertencimento')
  if (scores.autonomy >= 4) retentionDrivers.push('valoriza autonomia e espaço de execução')
  if (scores.grit >= 4) retentionDrivers.push('mostra perseverança acima da média')
  if (scores.relatedness < 3) retentionDrivers.push('pode perder permanência em ambiente frio')
  if (scores.autonomy < 3) retentionDrivers.push('pode reagir mal a excesso de controle')
  if (!retentionDrivers.length) retentionDrivers.push('permanência dependerá bastante da liderança e da alocação concreta')

  return {
    fit,
    retention,
    fitRationale: fitDrivers.join(', '),
    retentionRationale: retentionDrivers.join(', '),
  }
}
